import styled, { css, keyframes } from "styled-components";
import { lighten, darken } from "polished";
import { isNumber } from "lodash";
import media from "styled-media-query";
import { MenuItem } from "@szhsin/react-menu";

const hasPadding = (props) => {
  const prop = Object.keys(props).find((p) => p.includes("padded"));
  if (prop) {
    const padding = prop.split("_");
    if (padding.length) return padding[1];
  }
  return false;
}

export const Page = styled.div`
  position: relative;
  height: 100%;
`;

export const PageContentPadding = styled.div`
  height: 100%;
`;

export const PageContentInner = styled.div`
  padding: 0;
  height: 100%;
  
  ${(props) => hasPadding(props) && css`
    padding: ${(props) => hasPadding(props)}px;
    padding-top: 15px;
    padding-bottom: 15px;
  `};
  ${(props) => props.padding && css`
    padding: ${props.padding};
  `};
`;

export const PageHeader = styled.div`
  width: 100%;
  text-align: left;
  padding: 15px 20px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  ${(props) => props.align && css`
    text-align: ${props.align};
  `};
  ${media.lessThan("small")`
    padding: 15px;
  `};

  &:empty {
    display: none;
  }
`;

export const HR = styled.hr`
  width: 100%;
  border-width: 0.1px;
  background-color: ${({ theme }) => theme.faintBorder};
`;

export const PageHeaderText = styled.div`
  font-size: 1.4em;
  color: ${({ theme }) => theme.headerTextColor};
  font-weight: 500;
  display: flex;
  flex-direction: row;
  align-items: center;

  ${(props) => props.nomargin && css`
    margin: 0;
  `};

  ${(props) => props.direction && css`
    flex-direction: ${props.direction};
  `};

  ${(props) => props.margintop && css`
    margin-top: ${props.margintop}px;
  `};

  ${(props) => props.marginbottom && css`
    margin-bottom: ${props.marginbottom}px;
  `};

  ${(props) => props.marginright && css`
    margin-right: ${props.marginright}px;
  `};
  ${(props) => props.marginleft && css`
    margin-left: ${props.marginleft}px;
  `};

  ${(props) => props.center && css`
    text-align: center;
  `};

  ${media.lessThan("small")`
    font-size: 1.2em;
    align-self: ${({ left }) => left ? "flex-start" : "center"};
  `};
`;
export const Badge = styled.span`
    padding: 3px 12px;
    background: ${({ theme }) => theme.disabled};
    box-shadow: var(--sjs-shadow-small, 0px 1px 2px 0px rgba(0, 0, 0, 0.15));
    color: ${({ theme }) => theme.headerTextColor};
    font-size: 0.8rem;
    border-radius: ${props => props.rounded ? '50%' : '2px'};
    font-weight: 600;
    margin: 0 2px;

    ${(props) => props.compact && css`
      padding: 3px 6px;
    `};

    ${(props) => props.expand && css`
      width: 100%;
      text-align: center;
      display: block;
    `};

    ${(props) => props.black && css`
      background: ${({ theme }) => theme.fadedBlack};
      color: ${({ theme }) => theme.white};

      ${({ light }) => light && css`
        background: ${({ theme }) => lighten(0.45, theme.fadedBlack)};
        color: ${({ theme }) => theme.fadedBlack};
      `};
    `};

    ${(props) => props.success && css`
      background: ${({ theme }) => theme.success};
      color: ${({ theme }) => theme.white};

      ${({ light }) => light && css`
        background: ${({ theme }) => lighten(0.45, theme.success)};
        color: ${({ theme }) => theme.success};
      `};
    `};
    ${(props) => props.error && css`
      background: ${({ theme }) => theme.errorRed};
      color: ${({ theme }) => theme.white};

      ${({ light }) => light && css`
        background: ${({ theme }) => lighten(0.35, theme.errorRed)};
        color: ${({ theme }) => theme.errorRed};
      `};
    `};
    ${(props) => props.warning && css`
      background: ${({ theme }) => theme.notificationYellow};
      color: ${({ theme }) => theme.headerTextColor};

      ${({ light }) => light && css`
        background: ${({ theme }) => lighten(0.35, theme.notificationYellow)};
        color: ${({ theme }) => theme.notificationYellow};
      `};
    `};
    ${(props) => props.themed && css`
      background: ${({ theme }) => theme.buttonBackground};
      color: ${({ theme }) => theme.buttonTextColor};

      ${({ light }) => light && css`
        background: ${({ theme }) => lighten(0.32, theme.buttonBackground)};
        color: ${({ theme }) => theme.buttonBackground};
      `};
    `};
    ${(props) => props.blue && css`
      background: ${({ theme }) => theme.neutral_blue};
      color: ${({ theme }) => theme.white};

      ${({ light }) => light && css`
        background: ${({ theme }) => lighten(0.33, theme.neutral_blue)};
        color: ${({ theme }) => theme.neutral_blue};
      `};
    `};

    ${media.lessThan("small")`
      overflow: hidden;
      white-space: nowrap;
      max-width: 100px;
      display: block;
      text-overflow: ellipsis;
    `};
`;
export const PageHeaderBadge = styled.div`
  font-size: 0.75rem;
  background: ${({ theme }) => theme.inputBackground};
  box-shadow: var(--sjs-shadow-small, 0px 1px 2px 0px rgba(0, 0, 0, 0.15));
  border: 0.1px solid ${({ theme }) => theme.faintBorder};
  color: ${({ theme }) => theme.headerTextColor};
  /* Pill + auto-grow logic: keep circle for 1-2 chars, expand horizontally for >2 */
  --_size: 25px;
  min-width: var(--_size);
  height: var(--_size);
  padding: 0 6px; /* horizontal breathing room when it expands */
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-left: 10px;
  align-self: end;
  line-height: 1;
  font-weight: 500;
  position: relative;
  /* Use large radius so when width grows it becomes a pill */
  border-radius: 999px;
  /* Prevent layout shift when switching between 2 and 3+ digits */
  transition: background .2s ease, color .2s ease, box-shadow .2s ease;

  /* Mono-ish spacing for consistency across digits while still inheriting font smoothing */
  font-variant-numeric: tabular-nums;
  -moz-font-feature-settings: "tnum" 1;
  font-feature-settings: "tnum" 1;

  /* Maintain perfect circle when content is <= 2 chars (pure CSS) */
  &::before {
    content: attr(data-chars);
    /* hidden measuring element to detect char count using ch units fallback */
    position: absolute;
    opacity: 0;
    pointer-events: none;
    font: inherit;
  }

  /* Consumers can optionally pass data-length / we'll auto set via JS elsewhere if needed; pure CSS fallback keeps circle for <=2 */
  /* When exactly 1 or 2 characters, enforce fixed width == height */
  &[data-length="1"],
  &[data-length="2"] {
    padding-left: 0;
    padding-right: 0;
    width: var(--_size);
  }

  ${(props) => props.width && css`
    --_size: ${props.width}px;
    min-width: var(--_size);
    height: var(--_size);
  `};

  ${(props) => props.fontSize && css`
    font-size: ${props.fontSize};
  `};

  ${(props) => props.resizeMobile && css`
    ${media.lessThan("small")`
      font-size: 0.7rem;
      --_size: 20px;
      min-width: var(--_size);
      height: var(--_size);
    `};
  `};
  ${(props) => props.success && css`
    background: ${({ theme }) => theme.success};
    color: ${({ theme }) => theme.white};
  `};
  ${(props) => props.danger && css`
    background: ${({ theme }) => theme.errorRed};
    color: ${({ theme }) => theme.white};
  `};
  ${(props) => props.themed && css`
    background: ${({ theme }) => theme.buttonBackground};
    color: ${({ theme }) => theme.buttonTextColor};
  `};
  ${(props) => props.outline && css`
    background: transparent;
    border: 1px solid ${({ theme }) => theme.buttonBackground};
    color: ${({ theme }) => theme.buttonBackground};
    box-shadow: none;
  `};
`;
export const PageHeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  height: 100%;

  ${(props) => props.height && css`
    height: ${isNumber(props.height) ? `${props.height}px` : props.height};
  `};

  ${(props) => props.full_height && css`
    height: 100vh;
  `};

  ${(props) => props.paddingLeft && css`
    transition: padding-left 0.3s ease-in-out;
    padding-left: ${props.paddingLeft}px;
  `};
`;

export const Header = styled.div`
  font-size: 18px;
  font-weight: 500;
  z-index: 8;
  position: sticky;
  top: 0;
  width: 100%;

  ${(props) => !props.nobackground && css`
    box-shadow: var(--sjs-shadow-small, 0px 1px 2px 0px rgba(0, 0, 0, 0.15));
    background: ${({ theme }) => theme.footerColor};
  `};

  ${(props) => props.align && css`
    text-align: ${props.align};
  `};

  ${media.lessThan("small")`
    min-height: 55px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `};

  &:empty {
    display: none;
  }
`;
export const Section = styled.div`
  flex: 2;
  overflow: auto;
  height: 100%;
  position: relative;
  transition: all 0.3s ease-in-out;

  ${(props) => props.align && css`
    text-align: ${props.align};
  `};

  ${(props) => props.row && css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    gap: 0;
  `};
`;

export const Text = styled.div`
  color: ${({ theme }) => theme.headerTextColor};
  width: 100%;
  ${(props) => props.success && css`
    color: ${({ theme }) => theme.success};
  `};
  ${(props) => props.error && css`
    color: ${({ theme }) => theme.errorRed};
  `};
  ${(props) => props.warning && css`
    color: ${({ theme }) => theme.notificationYellow};
  `};
  ${(props) => props.themed && css`
    color: ${({ theme }) => theme.buttonBackground};
  `};
  ${(props) => props.disabled && css`
    color: ${({ theme }) => theme.themedDisabledText};
  `};
  ${(props) => props.slate && css`
    color: ${({ theme }) => theme.slate};
  `};
  ${(props) => props.muted && css`
    color: ${({ theme }) => theme.lineGrey};
  `};
  ${(props) => props.fontWeight && css`
    font-weight: ${props.fontWeight};
  `};
  ${(props) => props.wrap && css`
    white-space: pre-wrap;
  `};
  ${(props) => props.fontSize && css`
    font-size: ${props.fontSize};
  `};
  ${(props) => props.lineHeight && css`
    line-height: ${props.lineHeight};
  `};
  ${(props) => props.borderTop && css`
    border-top: 1px solid ${props.theme.faintBorder};
  `};
${(props) => props.width && css`
    min-width: ${props.width}px;
    max-width: 100%;
    margin: auto;
  `};
${(props) => props.widthPercent && css`
    min-width: ${props.widthPercent}%;
    width: ${props.widthPercent}%;
    margin: auto;
  `};
${(props) => props.nomargin && css`
    margin: 0;
  `};

  ${(props) => props.margintop && css`
    margin-top: ${props.margintop}px;
  `};

  ${(props) => props.marginbottom && css`
    margin-bottom: ${props.marginbottom}px;
  `};

  ${(props) => props.marginright && css`
    margin-right: ${props.marginright}px;
  `};
  ${(props) => props.marginleft && css`
    margin-left: ${props.marginleft}px;
  `};
${(props) => props.marginTop && css`
    margin-top: ${props.marginTop}px;
  `};
${(props) => props.height && css`
    max-height: ${props.height}px;
  `};
  ${(props) => props.padding && css`
    padding: ${props.padding};
  `};
${(props) => props.color && css`
    color: ${props.color};
  `};
`;

export const Ellipsis = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Bold = styled.span`
  font-weight: 700;

  ${(props) => props.pointer && css`
    cursor: pointer;
  `};

  ${(props) => props.link && css`
    color: ${({ theme }) => theme.link};
  `};

  ${(props) => props.success && css`
    color: ${({ theme }) => theme.success};
  `};
  ${(props) => props.error && css`
    color: ${({ theme }) => theme.errorRed};
  `};
  ${(props) => props.warning && css`
    color: ${({ theme }) => theme.notificationYellow};
  `};
  ${(props) => props.themed && css`
    color: ${({ theme }) => theme.buttonBackground};
  `};

  ${(props) => props.display && css`
    display: ${props.display};
  `};

  ${(props) => props.flex && css`
    display: flex;
    align-items: center;
    justify-content: ${props.flex || "center"};
    flex-direction: row;
    align-self: center;
    gap: 10px;
  `};

  ${(props) => props.between && css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    align-self: center;
    width: 100%;
  `};
  ${(props) => props.ellipsis && css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `};
`;

export const Footer = styled.div`
  -webkit-transform: translate3d(0,0,1px);
  transform: translate3d(0,0,1px);
  z-index: 7;
  position: sticky;
  bottom: 0;
  box-shadow: var(--sjs-shadow-small, 0px 1px 2px 0px rgba(0, 0, 0, 0.15));

  ${(props) => props.align && css`
    text-align: ${props.align};
  `};
  
  &:empty {
    display: none;
  }
`;

export const Button = styled.button`
  box-shadow: var(--sjs-shadow-small, 0px 1px 2px 0px rgba(0, 0, 0, 0.15));
  border: none;
  outline: 0;
  box-sizing: border-box;
  -webkit-box-sizing:border-box;
  -moz-box-sizing: border-box;
  background: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.default_background};
  font-family: Roboto, sans-serif;
  border-color: transparent;
  border-width: 2px;
  border-style: solid;
  padding: 9px 30px;
  border-radius: 3px;
  font-size: 15px;
  font-weight: 400;
  margin: 0 5px;
  cursor: pointer;
  transition: 0.3s ease;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;

  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */

  svg {
    margin-right: 10px;
    padding-left: 10px;
    ${(props) => props.iconOnly && css`
      margin-right: 0;
      padding-left: 0;
      gap: 10px;
    `};
  }

  ${(props) => props.iconOnly && css`
    gap: 10px;
  `};

  ${media.lessThan("small")`
    ${(props) => props.small && css`
      font-size: 0.7rem;
    `};
    ${(props) => props.smallGroup && css`
      font-size: 0.7rem;
      padding: 5px 0px;
    `};
  `};

   ${(props) => props.small && css`
    padding: 4px 10px;
    font-size: 11px;
  `};

  ${(props) => props.fontWeight && css`
    font-weight: ${props.fontWeight};
  `};

  ${(props) => props.small && props.outline && css`
    padding: 6px 10px;
    font-size: 11px;
  `};

  ${(props) => props.danger && css`
    background: ${({ theme }) => theme.errorRed};
    color: ${({ theme }) => theme.white};
  `};

  ${(props) => props.orange && css`
    background: ${({ theme }) => theme.button_accent};
    color: ${({ theme }) => theme.white};
  `};

  ${(props) => props.width && css`
    min-width: ${props.width}px;
    max-width: 100%;
    margin: auto;
  `};

  ${(props) => props.round && css`
    border-radius: 50%;
  `};

  ${(props) => props.widthPercent && css`
    min-width: ${props.widthPercent}%;
    width: ${props.widthPercent}%;
    margin: auto;
  `};

  ${(props) => props.maxWidth && css`
    max-width: ${props.maxWidth}px;
    width: 100%;
    margin: auto;

    ${media.lessThan("small")`
      max-width: 100%;
      width: 100%;
    `};
  `};

  ${(props) => props.expand && css`
    width: 100%;
    margin: auto;
  `};

  ${(props) => props.auto && css`
    width: auto;
    max-width: 100%;
    padding: 10px;
    margin: auto;
    ${(props) => props.small && css`
      padding: 4px;
    `};
  `};

  ${(props) => props.nomargin && css`
    margin: 0;
  `};

  ${(props) => props.margintop && css`
    margin-top: ${props.margintop}px;
  `};

  ${(props) => props.marginbottom && css`
    margin-bottom: ${props.marginbottom}px;
  `};

  ${(props) => props.marginright && css`
    margin-right: ${props.marginright}px;
  `};
  ${(props) => props.marginleft && css`
    margin-left: ${props.marginleft}px;
  `};

  ${(props) => props.primary && css`
    background: ${({ theme }) => theme.buttonBackground};
    color: ${({ theme }) => theme.buttonTextColor};
  `};

  ${(props) => props.success && css`
    background: ${({ theme }) => theme.success};
    color: ${({ theme }) => theme.white};
  `};

  ${(props) => props.green && css`
    background: ${({ theme }) => theme.buttonLightGreen};
    color: ${({ theme }) => theme.white};
  `};

  ${(props) => props.google && css`
    background: ${({ theme }) => theme.google};
    color: ${({ theme }) => theme.white};
  `};

  ${(props) => props.dropbox && css`
    background: ${({ theme }) => theme.dropbox};
    color: ${({ theme }) => theme.white};
  `};

  ${(props) => props.warning && css`
    background: ${({ theme }) => theme.noticeYellow};
    color: ${({ theme }) => theme.noticeYellowBackground};
  `};

    ${(props) => props.primary && css`
    background: ${({ theme }) => theme.buttonBackground};
    color: ${({ theme }) => theme.buttonTextColor};

    ${(props) => props.light && css`
      background: ${({ theme }) => lighten(0.7, theme.buttonBackground)};
      color: ${({ theme }) => theme.buttonTextColor};
      font-weight: 500;

      ${(props) => props.danger && css`
        background: ${({ theme }) => lighten(0.38, theme.errorRed)};
        color: ${({ theme }) => lighten(0.1, theme.errorRed)};
      `};
    `};
  `};

  ${(props) => props.outline && css`
    ${(props) => props.google && css`
      background: ${({ theme }) => lighten(0.3, theme.google)};
      color: ${({ theme }) => theme.google};
      box-shadow: 0 2px 3px rgba(0,0,0,0.1);
    `};
  
    ${(props) => props.dropbox && css`
      background: ${({ theme }) => lighten(0.3, theme.dropbox)};
      color: ${({ theme }) => theme.dropbox};
      box-shadow: 0 2px 3px rgba(0,0,0,0.1);
    `};
    ${(props) => props.primary && css`
      background: ${({ theme }) => lighten(0.3, theme.buttonBackground)};
      color: ${({ theme }) => theme.buttonBackground};
      border-width: 0px;
      box-shadow: 0 2px 3px rgba(0,0,0,0.1);
    `};
    ${(props) => props.danger && css`
      background: ${({ theme }) => lighten(0.35, theme.errorRed)};
      color: ${({ theme }) => theme.errorRed};
      border-width: 0px;
      box-shadow: 0 2px 3px rgba(0,0,0,0.1);
    `};
    ${(props) => props.orange && css`
      background: ${({ theme }) => lighten(0.35, theme.button_accent)};
      color: ${({ theme }) => theme.button_accent};
      border-width: 0px;
      box-shadow: 0 2px 3px rgba(0,0,0,0.1);

      &:hover {
        background: rgba(0,0,0,0.04);
      }
    `};
    ${(props) => props.warning && css`
      background: ${({ theme }) => lighten(0.07, theme.noticeYellow)};
      color: ${({ theme }) => theme.noticeYellowBackground};
      border-width: 0px;
      box-shadow: 0 2px 3px rgba(0,0,0,0.1);
    `};
  `};

  ${(props) => props.marginTop && css`
    margin-top: ${props.marginTop}px;
  `};

  ${(props) => props.rounded && css`
    border-radius: 30px;
  `};

  ${(props) => props.radius && css`
    border-radius: ${props.radius}px;
  `};

  ${(props) => props.disabled && css`
    color: ${({ theme }) => theme.labelGrey};
    background: ${({ theme }) => theme.disabled};
    cursor: no-drop;
  `};

  ${(props) => props.pulse && css`
    -webkit-animation: ${breatheAnimation} 2s ease-out infinite normal;
    animation: ${breatheAnimation} 2s ease-out infinite normal;
    &:hover {
      animation: none;
      -webkit-animation: none;
    }
  `};
  ${(props) => props.blink && css`
    -webkit-animation: ${blink} 2s linear 25;
    animation: ${blink} 2s linear 25;
    &:hover {
      animation: none;
      -webkit-animation: none;
    }
  `};

  ${(props) => props.height && css`
    max-height: ${props.height}px;
  `};
  ${(props) => props.padding && css`
    padding: ${props.padding};
  `};

  ${(props) => props.color && css`
    color: ${props.color};
  `};
  ${(props) => props.background && css`
    background: ${props.background};
  `};
  ${(props) => props.alignSelf && css`
    align-self: ${props.alignSelf};
  `};

`;

const blink = ({ theme }) => keyframes`
  0% { box-shadow: 0 0 25px ${theme.buttonBackground}; }
  50% { box-shadow: none; }
`;

const breatheAnimation = keyframes`
  0% {
    -webkit-transform: scale(1);
    -ms-transform: scale(1);
    transform: scale(1);
  }

  25% {
    -webkit-transform: scale(1.05);
    -ms-transform: scale(1.05);
    transform: scale(1.05);
  }

  60% {
    -webkit-transform: scale(1);
    -ms-transform: scale(1);
    transform: scale(1);
  }

  100% {
    -webkit-transform: scale(1);
    -ms-transform: scale(1);
    transform: scale(1);
  }
`;

export const RequiredStar = styled.div`
  color: red;
  margin-right: 2px;
  text-align: right;
  font-size: 11px;
  display: inline-block;

  ${(props) => !props.static && css`
    position: absolute;
    left: 10px;
    margin: auto;
    top: 0;
    bottom: 0;
    height: max-content;
  `};
`;

export const InputWrapper = styled.div`
  margin-bottom: 25px;
  padding: 5px;
  text-align: left;
  position:relative;
  align-self: start;

  ${(props) => props.sticky && css`
    position: sticky;
    top: ${(({ top }) => `${top}px`)};
    background-color: ${({ theme }) => theme.backgroundColor};
    z-index: 100;
  `};

  ${(props) => props.width && css`
    width: ${props.width}%;
  `};
  ${(props) => props.nomargin && css`
    margin: 0;
  `};
  ${(props) => props.nopadding && css`
    padding: 0;
  `};
  ${(props) => props.marginbottom && css`
    margin-bottom: ${props.marginbottom}px;
  `};
  ${(props) => props.margintop && css`
    margin-top: ${props.margintop}px;
  `};
  ${(props) => props.marginleft && css`
    margin-left: ${props.marginleft}px;
  `};
  ${(props) => props.padding && css`
    padding: ${props.padding};
  `};
  ${(props) => props.paddingleft && css`
    padding-left: ${props.paddingleft}px;
  `};
  ${(props) => props.paddingtop && css`
    padding-top: ${props.paddingtop}px;
  `};
  ${(props) => props.borderbottom && css`
    border-bottom: ${props.borderbottom};
    padding-bottom: 15px;
  `};
`;

export const InputLabel = styled.label`
  color: ${({ theme }) => theme.headerTextColor};
  font-size: 11px;
  display: block;
  cursor: pointer;
  margin-bottom: 5px;

  ${(props) => props.theme_color && css`
    color: ${({ theme }) => theme.hintColor};
  `};

  ${(props) => props.white && css`
    color: ${({ theme }) => theme.white};
  `};

  ${(props) => props.capitalize && css`
    text-transform: capitalize;
  `};

  ${(props) => props.error && css`
    color: ${({ theme }) => theme.errorRed};
  `};

  ${(props) => props.marginbottom && css`
    margin-bottom: ${props.marginbottom}px;
  `};
  ${(props) => props.marginleft && css`
    margin-left: ${props.marginleft}px;
  `};
  ${(props) => props.margintop && css`
    margin-top: ${props.margintop}px;
  `};
  ${(props) => props.size && css`
    font-size: ${props.size};
  `};
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  input {
    flex: 1;
  }
`;

export const Appendage = styled.div`
    display: flex;
    align-items: center;
    padding: 5px;
    justify-content: start;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.buttonBackground};
`;

export const Input = styled.input`
  box-shadow: 0 0.125em 0.313em rgba(50,50,93,.09), 0 0.063em 0.125em rgba(0,0,0,.07);
  border: none;
  outline: 0;
  width: 100%;
  box-sizing: border-box;
  -webkit-box-sizing:border-box;
  background: ${({ theme }) => theme.inputBackground};
  color: ${({ theme }) => theme.headerTextColor};
  border: 1px solid ${({ theme }) => theme.faintBorder};
  -moz-box-sizing: border-box;
  padding: 10px;
  border-radius: 4px;
  display: inline-block;
  font-family: inherit;
  font-size: 14px;
  position: relative;

  ${(props) => !["range", "radio", "checkbox"].includes(props.type) && css`
    -webkit-appearance: none !important;
  `};

  ${(props) => props.required && css`
     padding: 10px 20px;
  `};

  ${(props) => props.width && css`
    width: 100%;
    max-width: ${props.width};
  `};

  ${(props) => props.auto && css`
    width: auto;
  `};

  ${(props) => props.height && css`
     height: ${props.height};
  `};

  ${(props) => props.align && css`
     text-align: ${props.align};
  `};

  ${(props) => props.marginBottom && css`
     margin-bottom: ${props.marginBottom};
  `};

  ${(props) => props.flush && css`
     border-top-left-radius: 0;
     border-top-right-radius: 0;
  `};

  ${(props) => props.checkbox && css`
    box-shadow: none;
    margin: 0;
    padding: 0;
    width: auto;
    vertical-align: text-top;
    margin-right: 10px;
    height: 15px;
    width: 15px;
  `};
`;

export const Textarea = styled.textarea`
  box-shadow: 0 0.125em 0.313em rgba(50,50,93,.09), 0 0.063em 0.125em rgba(0,0,0,.07);
  border: none;
  outline: 0;
  width: 100%;
  box-sizing: border-box;
  -webkit-box-sizing:border-box;
  -moz-box-sizing: border-box;
  padding: 10px;
  border-radius: 4px;
  display: inline-block;
  resize: none;
  font-family: inherit;
  font-size: 14px;

  ${(props) => props.required && css`
     padding: 10px 20px;
  `};

  ${(props) => props.expand && css`
     height: 100%;
  `};

  ${(props) => props.width && css`
     width: ${props.width};
  `};
  ${(props) => props.resize && css`
     resize: vertical;
  `};
`;

export const Label = styled.label`
  font-size: 12px;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 5px;

  ${(props) => props.inline && css`
    flex-direction: row;
  `};
`;

export const FormSection = styled.div`
  margin-bottom: 10px;
  justify-content: center;
  width: 100%;

  ${(props) => props.checkbox && css`
    align-self: self-start;
  `};
`;

export const MenuItemInner = styled.div`
  font-size: 0.9rem;
  font-weight: 400;
  color: ${({ theme }) => theme.headerTextColor};
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
  flex-direction: row;
  gap: ${({ gap }) => gap || "5px"};

  a {
    color: ${({ theme }) => theme.buttonBackground};
    text-decoration: none;
  }

  ${(props) => props.danger && css`
    color: ${({ theme }) => theme.errorRed};
  `};

  svg {
    margin-right: 8px;
  }
`;

export const MenuItemCopy = styled(MenuItem)`
  ${(props) => props.active && css`
    background: ${({ theme }) => darken(0.1, theme.coreBackground)} !important;
  `};
  ${({ disabled }) => disabled && css`
    color: ${({ theme }) => theme.faintBorder} !important;

    ${MenuItemInner} {
      color: ${({ theme }) => theme.faintBorder} !important;
    }
  `};
`;

export const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.headerTextColor};
  font-weight: 600;
  padding: 5px 0 0 5px;
  white-space: pre-wrap;

  ${(props) => props.flex && css`
    display: flex;
    width: 100%;
    align-items: ${props.align ? props.align : "center"};;
    justify-content: ${props.direction ? props.direction : "space-between"};
    flex-direction: ${props.column ? "column" : "row"};
    gap: ${props.gap ? props.gap : "10px"};
    padding: ${props.padding ? props.padding : "5px 10px"};
  `};

  ${(props) => props.inline && css`
    display: inline-block;
  `};

  ${(props) => props.center && css`
    text-align: center;
    width: 100%;
  `};

  ${(props) => props.fontSize && css`
    font-size: ${props.fontSize?.includes("px") ? `${props.fontSize}` : props.fontSize} !important;
  `};

  ${(props) => props.fontWeight && css`
    font-weight: ${props.fontWeight};
  `};

  ${(props) => props.left && css`
    text-align: left;
  `};

  ${(props) => props.right && css`
    text-align: right;
  `};

  ${(props) => props.nopadding && css`
    padding: 0;
  `};

  ${(props) => props.paddingTop && css`
    padding-top: ${props.paddingTop}px;
  `};

  ${(props) => props.paddingBottom && css`
    padding-bottom: ${props.paddingBottom}px;
  `};

  ${(props) => props.paddingLeft && css`
    padding-left: ${props.paddingLeft}px;
  `};

  ${(props) => props.paddingRight && css`
    padding-right: ${props.paddingRight}px;
  `};

  ${(props) => props.padding && css`
    padding: ${props.padding};
  `};

  ${(props) => props.nomargin && css`
    margin: 0;
  `};

  ${(props) => props.auto && css`
    margin: auto;
  `};

  ${(props) => props.marginTop && css`
    margin-top: ${props.marginTop}px;
  `};

  ${(props) => props.marginBottom && css`
    margin-bottom: ${props.marginBottom}px;
  `};

  ${(props) => props.marginLeft && css`
    margin-left: ${props.marginLeft}px;
  `};

  ${(props) => props.lineHeight && css`
    line-height: ${props.lineHeight};
  `};

  ${(props) => props.pointer && css`
    cursor: pointer;
  `};

  ${(props) => props.width && css`
    width: ${props.width};
  `};

  ${(props) => props.expand && css`
    width: 100%;
  `};

  a {
    color: ${({ theme }) => theme.buttonBackground};
    text-decoration: none;
  }

  ${(props) => props.theme && css`
    color: ${({ theme }) => theme.headerTextColor};
  `};
${(props) => props.muted && css`
    color: ${({ theme }) => lighten(0.15, theme.headerTextColor)};
    font-weight: 500;
  `};

  ${(props) => props.mutedSlate && css`
    color: ${({ theme }) => lighten(0.2, theme.slate)};
    font-weight: 500;
  `};

  ${(props) => props.error && css`
    color: ${({ theme }) => theme.errorRed} !important;

    span {
      &:hover {
        cursor: pointer;
        background: ${({ theme }) => lighten(0.5, theme.errorRed)};
      }
    }
  `};

  ${(props) => props.warning && css`
    color: ${({ theme }) => theme.noticeYellow} !important;

    span {
      &:hover {
        cursor: pointer;
        background: ${({ theme }) => lighten(0.5, theme.noticeYellow)};
      }
    }
  `};

  ${(props) => props.primary && css`
    color: ${({ theme }) => theme.buttonBackground} !important;

    span {
      &:hover {
        cursor: pointer;
        background: ${({ theme }) => lighten(0.5, theme.buttonBackground)};
      }
    }
  `};

  ${(props) => props.success && css`
    color: ${({ theme }) => theme.success} !important;

    span {
      &:hover {
        cursor: pointer;
        background: ${({ theme }) => lighten(0.5, theme.success)};
      }
    }
  `};

  svg {
    margin-right: 5px;
    ${(props) => props.fontSize && css`
      font-size: ${props.fontSize?.includes("px") ? `${props.fontSize}` : props.fontSize} !important;
    `};
  }

  span {
    ${(props) => props.border && css`
      border-style: dashed;
      padding: 4px 10px;
      margin-left: 3px;
      border-radius: 3px;
    `};
  }

  ${(props) => props.link && css`
    color: ${({ theme }) => theme.buttonBackground};
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => darken(0.2, theme.buttonBackground)};
      cursor: pointer;
    }
  `};

  ${(props) => props.nocolor && css`
    color: transparent;
  `};
  ${(props) => props.nounderline && css`
    text-decoration: none;
  `};
`;